import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export interface WorkflowEtapa {
  id: string;
  contrato_id: string;
  ordem: number;
  nome: string;
  perfil_aprovador: string;
  obrigatorio: boolean;
  prazo_dias: number | null;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkflowAprovacao {
  id: string;
  contrato_id: string;
  entidade_tipo: string;
  entidade_id: string;
  etapa_id: string;
  etapa_ordem: number;
  etapa_nome: string;
  status: 'pendente' | 'aprovado' | 'rejeitado' | 'pulado';
  aprovador_id: string | null;
  aprovador_nome: string | null;
  aprovador_email: string | null;
  aprovado_em: string | null;
  observacoes: string | null;
  dados_snapshot: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface WorkflowNotificacao {
  id: string;
  aprovacao_id: string;
  destinatario_id: string;
  tipo: string;
  mensagem: string;
  lida: boolean;
  lida_em: string | null;
  created_at: string;
}

const PERFIL_LABELS: Record<string, string> = {
  supervisor: 'Supervisor',
  gerente: 'Gerente',
  diretor: 'Diretor',
};

export const getPerfilLabel = (perfil: string) => PERFIL_LABELS[perfil] || perfil;

export const PERFIL_OPTIONS = Object.entries(PERFIL_LABELS).map(([value, label]) => ({ value, label }));

// ========== HOOKS ==========

export function useWorkflowEtapas(contratoId: string) {
  return useQuery({
    queryKey: ['emp-workflow-etapas', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_workflow_etapas')
        .select('*')
        .eq('contrato_id', contratoId)
        .eq('ativo', true)
        .order('ordem', { ascending: true });
      if (error) throw error;
      return data as WorkflowEtapa[];
    },
    enabled: !!contratoId,
  });
}

export function useWorkflowAprovacoes(entidadeTipo: string, entidadeId: string | null) {
  return useQuery({
    queryKey: ['emp-workflow-aprovacoes', entidadeTipo, entidadeId],
    queryFn: async () => {
      if (!entidadeId) return [];
      const { data, error } = await supabase
        .from('emp_workflow_aprovacoes')
        .select('*')
        .eq('entidade_tipo', entidadeTipo)
        .eq('entidade_id', entidadeId)
        .order('etapa_ordem', { ascending: true });
      if (error) throw error;
      return data as WorkflowAprovacao[];
    },
    enabled: !!entidadeId,
  });
}

export function useWorkflowNotificacoes() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['emp-workflow-notificacoes', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('emp_workflow_notificacoes')
        .select('*')
        .eq('destinatario_id', user.id)
        .eq('lida', false)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as WorkflowNotificacao[];
    },
    enabled: !!user,
  });
}

// ========== MUTATIONS ==========

export function useWorkflowMutations(contratoId: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const invalidate = (entidadeTipo?: string, entidadeId?: string) => {
    qc.invalidateQueries({ queryKey: ['emp-workflow-etapas', contratoId] });
    if (entidadeTipo && entidadeId) {
      qc.invalidateQueries({ queryKey: ['emp-workflow-aprovacoes', entidadeTipo, entidadeId] });
    }
    qc.invalidateQueries({ queryKey: ['emp-workflow-notificacoes'] });
  };

  // Add/update workflow steps
  const upsertEtapa = useMutation({
    mutationFn: async (etapa: { id?: string; ordem: number; nome: string; perfil_aprovador: string; obrigatorio?: boolean; prazo_dias?: number }) => {
      const payload = {
        contrato_id: contratoId,
        ordem: etapa.ordem,
        nome: etapa.nome,
        perfil_aprovador: etapa.perfil_aprovador,
        obrigatorio: etapa.obrigatorio ?? true,
        prazo_dias: etapa.prazo_dias ?? 5,
        updated_at: new Date().toISOString(),
      };

      if (etapa.id) {
        const { error } = await supabase.from('emp_workflow_etapas').update(payload).eq('id', etapa.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('emp_workflow_etapas').insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Etapa de aprovação salva' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao salvar etapa', description: e.message, variant: 'destructive' }),
  });

  const removeEtapa = useMutation({
    mutationFn: async (etapaId: string) => {
      const { error } = await supabase.from('emp_workflow_etapas').update({ ativo: false, updated_at: new Date().toISOString() }).eq('id', etapaId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Etapa removida' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao remover etapa', description: e.message, variant: 'destructive' }),
  });

  // Initiate workflow for an entity
  const iniciarWorkflow = useMutation({
    mutationFn: async ({ entidadeTipo, entidadeId }: { entidadeTipo: string; entidadeId: string }) => {
      // Get active etapas
      const { data: etapas, error: etapaError } = await supabase
        .from('emp_workflow_etapas')
        .select('*')
        .eq('contrato_id', contratoId)
        .eq('ativo', true)
        .order('ordem');
      if (etapaError) throw etapaError;
      if (!etapas?.length) throw new Error('Nenhuma etapa de aprovação configurada');

      // Create approval records
      const records = etapas.map(e => ({
        contrato_id: contratoId,
        entidade_tipo: entidadeTipo,
        entidade_id: entidadeId,
        etapa_id: e.id,
        etapa_ordem: e.ordem,
        etapa_nome: e.nome,
        status: 'pendente' as const,
      }));

      const { error: insertError } = await supabase.from('emp_workflow_aprovacoes').insert(records);
      if (insertError) throw insertError;

      return etapas.length;
    },
    onSuccess: (count, vars) => {
      invalidate(vars.entidadeTipo, vars.entidadeId);
      toast({ title: 'Workflow iniciado', description: `${count} etapa(s) de aprovação criadas.` });
    },
    onError: (e: Error) => toast({ title: 'Erro ao iniciar workflow', description: e.message, variant: 'destructive' }),
  });

  // Approve/reject a step
  const decidirAprovacao = useMutation({
    mutationFn: async ({ aprovacaoId, status, observacoes, entidadeTipo, entidadeId }: {
      aprovacaoId: string;
      status: 'aprovado' | 'rejeitado';
      observacoes?: string;
      entidadeTipo: string;
      entidadeId: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      // Get user profile for desnormalization
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, email')
        .eq('id', user.id)
        .single();
      
      const { error } = await supabase
        .from('emp_workflow_aprovacoes')
        .update({
          status,
          aprovador_id: user.id,
          aprovador_nome: profile?.name || null,
          aprovador_email: profile?.email || user.email || null,
          aprovado_em: new Date().toISOString(),
          observacoes: observacoes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', aprovacaoId);
      if (error) throw error;
      return { entidadeTipo, entidadeId };
    },
    onSuccess: (vars) => {
      invalidate(vars.entidadeTipo, vars.entidadeId);
      toast({ title: 'Decisão registrada' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao registrar decisão', description: e.message, variant: 'destructive' }),
  });

  // Mark notification as read
  const marcarLida = useMutation({
    mutationFn: async (notificacaoId: string) => {
      const { error } = await supabase
        .from('emp_workflow_notificacoes')
        .update({ lida: true, lida_em: new Date().toISOString() })
        .eq('id', notificacaoId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emp-workflow-notificacoes'] }),
  });

  return {
    upsertEtapa,
    removeEtapa,
    iniciarWorkflow,
    decidirAprovacao,
    marcarLida,
  };
}
